/**
 * Consola de roles (Fase 5): asignar y revocar roles con su alcance, incluido Revisor RAT (que la pantalla vieja de
 * Usuarios no puede asignar). Solo `user.manage` (Administrador RAT). El backend valida todo: aqui solo se ofrecen
 * combinaciones validas (catalogo de GET /admin/roles) y se traducen sus errores.
 * Nadie modifica sus propios roles (autoguardia del backend): sus filas no tienen "Revocar" y no aparece como destino.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import { ratRoleLabel, type RatRole } from '../auth/access';
import {
  fetchAdminUsers,
  fetchInstitutionalDashboard,
  fetchRoleAssignments,
  fetchRoleCatalog,
  grantRoleApi,
  revokeRoleApi,
  type RoleScopeType,
} from '../lib/queries';
import { errorMessage } from '../lib/errors';
import { ROLE_DESCRIPTION, SCOPE_TYPE_LABEL, scopeLabel, unitTypeLabel } from '../lib/roles';
import { PageHeader, QueryState } from '../components/ui';

export function RolesAdmin() {
  const { authz } = useAuth();
  const me = authz.user_id;
  const qc = useQueryClient();

  const catalogQ = useQuery({ queryKey: ['admin', 'role-catalog'], queryFn: fetchRoleCatalog });
  const assignQ = useQuery({ queryKey: ['admin', 'role-assignments'], queryFn: fetchRoleAssignments });
  const usersQ = useQuery({ queryKey: ['admin', 'users'], queryFn: fetchAdminUsers });
  const unitsQ = useQuery({ queryKey: ['dash', 'inst', 'units-for-admin'], queryFn: fetchInstitutionalDashboard });
  const units = (unitsQ.data?.por_unidad ?? []).map((u) => ({ id: u.unit_id, name: u.name }));

  const [userId, setUserId] = useState('');
  const [role, setRole] = useState<RatRole>('revisor_rat');
  const [scopeType, setScopeType] = useState<RoleScopeType>('global');
  const [scopeId, setScopeId] = useState('');
  const [descendants, setDescendants] = useState(true);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['admin', 'role-assignments'] });
    void qc.invalidateQueries({ queryKey: ['admin', 'users'] }); // la pantalla de Usuarios deriva sus flags de estos roles
  };

  // Alcances validos para el rol elegido: los da el backend (no se duplica su tabla aqui).
  const allowedScopes = catalogQ.data?.roles.find((r) => r.role === role)?.allowed_scopes ?? [];
  const effectiveScope: RoleScopeType | undefined = allowedScopes.includes(scopeType) ? scopeType : allowedScopes[0];
  const needsTarget = effectiveScope === 'unit' || effectiveScope === 'unit_type';
  const valid = !!userId && !!effectiveScope && (!needsTarget || !!scopeId);

  const grant = useMutation({
    mutationFn: grantRoleApi,
    onSuccess: () => {
      setNotice('Rol asignado.');
      setScopeId('');
      refresh();
    },
    onError: () => setNotice(null),
  });
  const revoke = useMutation({
    mutationFn: revokeRoleApi,
    onSuccess: () => {
      setNotice('Rol revocado.');
      setConfirmId(null);
      refresh();
    },
    onError: () => setConfirmId(null),
  });
  const error = grant.error ?? revoke.error;

  const rows = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const all = assignQ.data ?? [];
    if (!q) return all;
    return all.filter((a) => `${a.email ?? ''} ${ratRoleLabel(a.role)} ${scopeLabel(a)}`.toLowerCase().includes(q));
  }, [assignQ.data, filter]);

  function submit() {
    if (!valid || !effectiveScope) return;
    setNotice(null);
    grant.mutate({
      user_id: userId,
      role,
      scope_type: effectiveScope,
      scope_id: needsTarget ? scopeId : null,
      ...(needsTarget ? { include_descendants: descendants } : {}),
    });
  }

  return (
    <>
      <PageHeader
        eyebrow="Configuración institucional"
        title="Roles y permisos"
        sub="Quién puede hacer qué, y dónde. Cada rol se asigna con un alcance: toda la institución, una categoría de unidades o una unidad."
      />

      {error && (
        <div className="callout callout--crit" style={{ marginBottom: 12 }}>
          {errorMessage(error)}
        </div>
      )}
      {notice && (
        <div className="callout" style={{ marginBottom: 12 }}>
          {notice}
        </div>
      )}

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card__b">
          <div className="eyebrow">Asignar un rol</div>
          <QueryState isLoading={catalogQ.isLoading || usersQ.isLoading} error={catalogQ.error ?? usersQ.error}>
            <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', marginTop: 8 }}>
              <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
                Persona
                <select value={userId} onChange={(e) => setUserId(e.target.value)}>
                  <option value="">Seleccione…</option>
                  {(usersQ.data ?? [])
                    .filter((u) => u.id !== me)
                    .map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.email}
                      </option>
                    ))}
                </select>
              </label>

              <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
                Rol
                <select
                  value={role}
                  onChange={(e) => {
                    setRole(e.target.value as RatRole);
                    setScopeId('');
                  }}
                >
                  {(catalogQ.data?.roles ?? []).map((r) => (
                    <option key={r.role} value={r.role}>
                      {ratRoleLabel(r.role)}
                    </option>
                  ))}
                </select>
              </label>

              <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
                Alcance
                <select
                  value={effectiveScope ?? ''}
                  disabled={allowedScopes.length <= 1}
                  onChange={(e) => {
                    setScopeType(e.target.value as RoleScopeType);
                    setScopeId('');
                  }}
                >
                  {allowedScopes.map((s) => (
                    <option key={s} value={s}>
                      {SCOPE_TYPE_LABEL[s]}
                    </option>
                  ))}
                </select>
              </label>

              {effectiveScope === 'unit' && (
                <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
                  Unidad
                  <select value={scopeId} onChange={(e) => setScopeId(e.target.value)}>
                    <option value="">Seleccione…</option>
                    {units.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {effectiveScope === 'unit_type' && (
                <label style={{ display: 'grid', gap: 4, fontSize: 13 }}>
                  Categoría
                  <select value={scopeId} onChange={(e) => setScopeId(e.target.value)}>
                    <option value="">Seleccione…</option>
                    {(catalogQ.data?.unit_types ?? []).map((t) => (
                      <option key={t} value={t}>
                        {unitTypeLabel(t)}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>

            <p className="muted" style={{ fontSize: 12.5, margin: '10px 0 0' }}>
              {ROLE_DESCRIPTION[role]}
            </p>

            <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', marginTop: 10 }}>
              {needsTarget && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                  <input type="checkbox" checked={descendants} onChange={(e) => setDescendants(e.target.checked)} />
                  Incluir las subunidades que dependen de la elegida
                </label>
              )}
              <span style={{ flex: 1 }} />
              <button className="btn btn--primary" disabled={!valid || grant.isPending} onClick={submit}>
                {grant.isPending ? 'Asignando…' : 'Asignar rol'}
              </button>
            </div>
          </QueryState>
        </div>
      </div>

      <div className="card">
        <div className="card__b">
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <div className="eyebrow" style={{ flex: 1 }}>
              Asignaciones vigentes{assignQ.data ? ` (${assignQ.data.length})` : ''}
            </div>
            <input
              placeholder="Filtrar por correo, rol o unidad"
              aria-label="Filtrar asignaciones"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              style={{ minWidth: 240 }}
            />
          </div>
          <QueryState isLoading={assignQ.isLoading} error={assignQ.error}>
            {rows.length === 0 ? (
              <p className="muted" style={{ fontSize: 12.5 }}>
                {filter ? 'Ninguna asignación coincide con el filtro.' : 'Aún no hay asignaciones.'}
              </p>
            ) : (
              <div className="tablewrap" style={{ border: 0, marginTop: 8 }}>
                <table className="data">
                  <thead>
                    <tr>
                      <th>Persona</th>
                      <th>Rol</th>
                      <th>Alcance</th>
                      <th>Otorgado</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((a) => {
                      const own = a.user_id === me;
                      return (
                        <tr key={a.id}>
                          <td>{a.email ?? a.user_id}</td>
                          <td>
                            <span className={`pill ${a.role === 'admin_rat' ? 'pill--accent' : 'pill--neutral'}`}>{ratRoleLabel(a.role)}</span>
                          </td>
                          <td>{scopeLabel(a)}</td>
                          <td className="muted" style={{ fontSize: 12 }}>
                            {a.granted_at.slice(0, 10)}
                          </td>
                          <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                            {own ? (
                              <span className="muted" style={{ fontSize: 12 }} title="Nadie puede modificar sus propios roles">
                                Su cuenta
                              </span>
                            ) : confirmId === a.id ? (
                              <>
                                <button className="btn btn--primary" disabled={revoke.isPending} onClick={() => revoke.mutate(a.id)}>
                                  {revoke.isPending ? 'Revocando…' : 'Confirmar revocación'}
                                </button>{' '}
                                <button className="btn btn--ghost" disabled={revoke.isPending} onClick={() => setConfirmId(null)}>
                                  Cancelar
                                </button>
                              </>
                            ) : (
                              <button
                                className="btn btn--ghost"
                                onClick={() => {
                                  setNotice(null);
                                  setConfirmId(a.id);
                                }}
                              >
                                Revocar
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </QueryState>
        </div>
      </div>
    </>
  );
}
